import type { CentralIconBaseProps } from "central-icons/CentralIconBase";
import type { LucideIcon } from "lucide-react";
import type { FC } from "react";

import type { PropsWithClassName } from "@/components/podkit/lib/cn";

export type Size = "sm" | "base" | "lg";

export type BaseIconProps = {
    size: Size;
} & React.SVGProps<SVGSVGElement> &
    PropsWithClassName;
export const fromGitpodSize = (size: BaseIconProps["size"]) => {
    switch (size) {
        case "sm":
            return 16;
        case "base":
            return 20;
        case "lg":
            return 24;
        default:
            return 16;
    }
};

/** Adapts a lucide-react icon to the BaseIconProps interface used by flexkit Button. */
export const withLucideIcon = (IconComponent: LucideIcon): FC<BaseIconProps> => {
    const Component = ({ size, className, ...rest }: BaseIconProps) => {
        const px = fromGitpodSize(size);
        return <IconComponent size={px} className={className} {...(rest as Record<string, unknown>)} />;
    };
    Component.displayName = `withLucideIcon(${IconComponent.displayName || IconComponent.name || "Icon"})`;
    return Component;
};

export const withGitpodSize = (
    IconComponent: FC<CentralIconBaseProps>,
): FC<Omit<CentralIconBaseProps, "size"> & BaseIconProps> => {
    const Component = (props: Omit<CentralIconBaseProps, "size"> & BaseIconProps) => {
        const { size, ...restProps } = props;
        const numericSize = fromGitpodSize(size);
        return <IconComponent size={numericSize} {...restProps} />;
    };
    Component.displayName = `withGitpodSize(${IconComponent.displayName || IconComponent.name || "Icon"})`;
    return Component;
};
